import { GetServerSideProps } from "next";
import Link from "next/link";
import { useState, useEffect } from "react";
import { PatternFormat } from "react-number-format";
import { useClipboard } from "use-clipboard-copy";
import {
  CallBoldIcon,
  CopyBoldIcon,
  SmsBoldIcon,
} from "../../../app/component/commons/icon/SvgIcon";
import Image from "../../../app/component/commons/image/Image";
import Meta from "../../../app/component/commons/meta/Meta";
import { DetailFacProps } from "../../../app/helpers/types/getTypes";
import DefaultLayout from "../../../app/layouts/DefaultLayout";
import { client } from "../../../app/utils/axios";
import { toast } from "react-toastify";
import { CardFacilityDetail } from "../../../app/component/commons/card/Card";
import Button from "../../../app/component/commons/button/Button";
import EmptyText from "../../../app/helpers/EmptyText";
import { useRouter } from "next/router";

interface Props {
  params: DetailFacProps;
}

const Index = ({ params }: Props) => {
  const router = useRouter();
  const clipboard = useClipboard();
  const [mode, setMode] = useState("");

  useEffect(() => {
    // window.analytics.page("viewed");
    if (window.innerWidth < 768) {
      setMode("mobile");
    } else {
      setMode("desktop");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Meta
        title={`Storage Unit Auctions Happening At ${params.name}, ${params.city.name} | StorageUnitAuctionList.com`}
        description={`Find Storage Unit Auctions happening at ${params.name} in ${params.city.name} and every major city and state in the United States. Signup in minutes to get access to the most up to date list of storage unit auctions happening near you.`}
      >
        <meta
          name="keywords"
          content={`Storage Unit Auctions At ${params.name} in ${params.city.name}, Find Storage Unit Auctions in ${params.city.name}, Storage Unit Auctions Happening in ${params.city.name}`}
        />
      </Meta>
      <DefaultLayout>
        <div className="container mx-auto">
          <div className="mx-auto mt-7 lg:mt-[5.75rem] lg:px-[10.938rem] px-4.5">
            <div className="flex flex-col-reverse lg:flex-row lg:space-x-5">
              <div className="mt-7 lg:mt-0 lg:w-7/12 h-[19.25rem] md:h-[24.375rem]">
                <iframe
                  className="rounded-3xl"
                  title="Lokasi pada peta"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps/embed/v1/place?key=${process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY}&q=${params.address},${params.zipcode},${params.city.name},${params.city.state.name}&region=US`}
                />
              </div>
              <div className="w-full lg:w-5/12">
                <div className="flex flex-row items-start space-x-2.5 ">
                  <div className="w-12 min-w-[3rem]">
                    <Image
                      src={params.image.md}
                      alt={params.name}
                      className="w-full h-12 rounded-2lg object-cover"
                    />
                  </div>
                  <div className="flex flex-col space-y-1">
                    <div className="text-regular-normal text-neutral-600 uppercase">
                      storage facility
                    </div>
                    <p className="text-large-tight text-neutral-900 font-bold">
                      {params.name}
                    </p>
                  </div>
                </div>
                <div className="mt-3 py-3 border-y border-neutral-200 flex flex-col space-y-3">
                  <div className="font-medium text-neutral-900 text-large-normal">
                    Location
                  </div>
                  <div className="flex flex-row items-center justify-between">
                    <div className="w-1/3 text-tiny-normal text-neutral-900">
                      {"State & City"}
                    </div>
                    <div className="w-2/3 text-right text-tiny-normal text-neutral-700">
                      {params.city.state.name}, {params.city.name}
                    </div>
                  </div>
                  <div className="flex flex-row items-center justify-between">
                    <div className="w-1/3 text-tiny-normal text-neutral-900">
                      Zipcode
                    </div>
                    <div className="w-2/3 text-right text-tiny-normal text-neutral-700">
                      {params.zipcode}
                    </div>
                  </div>
                  <Link
                    href={`https://www.google.com/maps?q=${params.address},${params.zipcode},${params.city.name},${params.city.state.name}`}
                  >
                    <a
                      target="_blank"
                      className="flex flex-row items-start space-x-2 cursor-pointer"
                    >
                      <div className="w-12 min-w-[3rem]">
                        <Image
                          src="/images/map/MapPlaceholder.svg"
                          alt={params.name}
                          className="w-full h-12 rounded-xl object-cover"
                        />
                      </div>
                      <div className="flex flex-col space-y-2 w-auto">
                        <div className="text-tiny-normal text-neutral-600">
                          Located at
                        </div>
                        <div className="text-small-normal font-medium text-neutral-900 break-all">
                          {params.address}
                        </div>
                      </div>
                    </a>
                  </Link>
                </div>
                <div className="border-b border-neutral-200 py-3 flex flex-col space-y-3">
                  <div className="text-large-normal font-medium text-neutral-900">
                    Contact Information
                  </div>

                  <div className="grid grid-cols-1 gap-y-3.5 mt-3">
                    <div className="flex flex-row justify-between space-x-2 items-start">
                      <Link
                        href={
                          params.phone_number
                            ? `tel:+1${params.phone_number}`
                            : "#"
                        }
                      >
                        <div className="flex flex-row space-x-2 items-center cursor-pointer">
                          <div className="p-2.5 bg-info-500 text-white rounded-2lg">
                            <CallBoldIcon size="20" />
                          </div>
                          <div className="flex flex-col space-y-1.5">
                            <div className="text-tiny-none text-neutral-600 line-clamp-1">
                              Phone Number
                            </div>

                            <EmptyText trigger={params.phone_number}>
                              <div className="text-regular-tight font-medium text-neutral-900">
                                <PatternFormat
                                  value={params.phone_number}
                                  displayType={"text"}
                                  format="+1 ### ### ####"
                                />
                              </div>
                            </EmptyText>
                          </div>
                        </div>
                      </Link>
                      <div
                        className={`text-neutral-500 cursor-pointer ${
                          !params.phone_number ? "hidden" : ""
                        }`}
                        title="Copy"
                        onClick={() => {
                          clipboard.copy(`+1${params.phone_number}`);

                          toast.success("Copied!", {
                            hideProgressBar: true,
                            autoClose: 1000,
                            position: "bottom-center",
                          });
                        }}
                      >
                        <CopyBoldIcon size="19" />
                      </div>
                    </div>
                    {/* <div className="flex flex-row justify-between space-x-2 items-start">
                      <Link
                        href={params.email ? `mailto:${params.email}` : "#"}
                      >
                        <div className="flex flex-row space-x-2 items-center cursor-pointer">
                          <div className="p-2.5 bg-danger-500 text-white rounded-2lg">
                            <SmsBoldIcon size="20" />
                          </div>
                          <div className="flex flex-col space-y-1.5">
                            <div className="text-tiny-none text-neutral-600">
                              Email Address
                            </div>

                            <EmptyText trigger={params.email}>
                              <div className="text-regular-tight font-medium text-neutral-900 break-all">
                                {params.email}
                              </div>
                            </EmptyText>
                          </div>
                        </div>
                      </Link>
                      <div
                        className={`text-neutral-500 cursor-pointer ${
                          !params.email ? "hidden" : ""
                        }`}
                        title="Copy"
                        onClick={() => {
                          clipboard.copy(params.email);

                          toast.success("Copied!", {
                            hideProgressBar: true,
                            autoClose: 1000,
                            position: "bottom-center",
                          });
                        }}
                      >
                        <CopyBoldIcon size="19" />
                      </div>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {params.upcoming_auctions.length > 0 && (
            <div className="mt-7 md:mt-11 gradient-main min-h-full lg:mx-[10.938rem] p-4.5 md:p-12 lg:rounded-t-[3rem] mx-auto relative flex flex-col items-center">
              <div className="absolute lg:right-[-3rem] lg:top-[7rem] top-[-2.5rem] right-[1rem] lg:block hidden">
                <Image
                  src="/images/box.svg"
                  alt="Box"
                  className="lg:w-[7.75rem] lg:h-[7.75rem] w-[4.75rem] h-[4.75rem]"
                />
              </div>
              <div className="absolute lg:left-[-4rem] lg:top-[2.5rem] left-[1rem] top-[-1rem] lg:block hidden">
                <Image
                  src="/images/illustration/01.svg"
                  alt="Box"
                  className="lg:w-[7.75rem] lg:h-[7.75rem] w-[4.75rem] h-[4.75rem]"
                />
              </div>
              <div className="font-bold text-neutral-900 text-title3 leading-9 md:!text-title1">
                Upcoming Auctions
              </div>
              <div className="mt-3 md:mt-3.5 text-neutral-700 text-small-normal md:text-large-normal leading-7 text-center md:px-0 px-8">
                {`Signup for a subscription so you don't miss auctions happening at
              this location`}
              </div>
              <div className="mt-3.5 md:mt-6 flex flex-col items-center space-y-4.5 md:w-auto w-full">
                {params.upcoming_auctions.map((e, key: number) => (
                  <CardFacilityDetail key={key} data={e} isBlur={key > 1} />
                ))}
              </div>
              <div className="mt-[2.375rem] md:mt-12">
                <Link
                  href={`${process.env.NEXT_PUBLIC_BUYER_URL}/b/subscription/add/location?lat=${params.lat}&lng=${params.lng}`}
                >
                  <a target="_blank">
                    <Button size="lg" className="!rounded-full w-[10.688rem]">
                      Subscribe
                    </Button>
                  </a>
                </Link>
              </div>
            </div>
          )}
        </div>
      </DefaultLayout>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  let params = {};

  try {
    const { data } = await client.get(`/m/facility/detail`, {
      ...context,
      params: {
        slug: context.query.slug,
      },
    });

    if (data) {
      params = data;
    }

    return {
      props: { params },
    };
  } catch (error: any) {
    if (error.response) {
      return {
        redirect: {
          permanent: false,
          destination: `/${error.response.status}`,
        },
      };
    }

    return {
      redirect: {
        permanent: false,
        destination: "/404",
      },
    };
  }
};

export default Index;
